import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  MapPin, Phone, Mail, Building2, Star, Globe, Award,
  Send, Trophy, Bed, Bath, Maximize, Loader2, CheckCircle2, ChevronRight,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

import { useBrand } from '../contexts/BrandContext';

export default function AgentPublicPage() {
  const { slug } = useParams();
  const { name: brandName } = useBrand();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '', interest: 'buyer_inquiry' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [sentMsg, setSentMsg] = useState('');

  const fetchPage = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/api/public/agent/${slug}`);
      setData(d);
    } catch {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => { fetchPage(); }, [fetchPage]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name) return;
    setSending(true);
    try {
      const { data: res } = await axios.post(`${API}/api/public/agent/${slug}/contact`, form);
      setSent(true);
      setSentMsg(res.message);
      setForm({ name: '', email: '', phone: '', message: '', interest: 'buyer_inquiry' });
    } catch {
      setSentMsg('Something went wrong. Please try again.');
    } finally {
      setSending(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-amber-400" />
    </div>
  );

  if (notFound) return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="text-center">
        <h1 className="text-3xl font-bold text-foreground mb-3">Agent Not Found</h1>
        <p className="text-muted-foreground">This agent page doesn't exist or is not public.</p>
      </div>
    </div>
  );

  const p = data.profile;
  const listings = data.featured_listings || [];
  const scores = data.deal_scores || {};

  return (
    <div className="min-h-screen bg-background">
      {/* SEO meta via document title */}
      <title>{p.full_name} — {p.brokerage_name} | {brandName}</title>

      {/* Hero */}
      <header className="relative overflow-hidden" data-testid="agent-hero">
        <div className="absolute inset-0 bg-gradient-to-br from-card via-slate-900 to-amber-950/20" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-8">
            {/* Headshot */}
            <div className="shrink-0" data-testid="agent-headshot">
              {p.headshot_url ? (
                <img src={p.headshot_url} alt={p.full_name}
                  className="w-32 h-32 sm:w-40 sm:h-40 rounded-2xl object-cover ring-2 ring-amber-500/30" />
              ) : (
                <div className="w-32 h-32 sm:w-40 sm:h-40 rounded-2xl bg-gradient-to-br from-amber-600 to-amber-800 flex items-center justify-center ring-2 ring-amber-500/30">
                  <span className="text-4xl sm:text-5xl font-bold text-foreground">
                    {p.full_name.split(' ').map(w => w[0]).join('').slice(0, 2)}
                  </span>
                </div>
              )}
            </div>

            {/* Info */}
            <div className="text-center sm:text-left flex-1">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground tracking-tight" data-testid="agent-name">
                {p.full_name}
              </h1>
              <p className="text-amber-400 font-medium mt-2 text-base sm:text-lg" data-testid="agent-brokerage">
                {p.brokerage_name}
              </p>
              <div className="flex flex-wrap items-center justify-center sm:justify-start gap-3 mt-4">
                <Badge className="bg-muted text-foreground border-border gap-1.5">
                  <MapPin size={12} /> {p.display_market}
                </Badge>
                {p.languages?.length > 0 && (
                  <Badge className="bg-muted text-foreground border-border gap-1.5">
                    <Globe size={12} /> {p.languages.join(', ')}
                  </Badge>
                )}
                {p.years_experience && (
                  <Badge className="bg-muted text-foreground border-border gap-1.5">
                    <Award size={12} /> {p.years_experience}+ years
                  </Badge>
                )}
              </div>
              {p.bio && (
                <p className="text-foreground mt-5 text-sm sm:text-base max-w-xl leading-relaxed" data-testid="agent-bio">
                  {p.bio}
                </p>
              )}

              {/* Contact buttons */}
              <div className="flex flex-wrap items-center justify-center sm:justify-start gap-3 mt-6">
                {p.personal_phone && (
                  <a href={`tel:${p.personal_phone}`}>
                    <Button className="bg-amber-600 hover:bg-amber-500 text-foreground gap-2" data-testid="call-agent-btn">
                      <Phone size={16} /> Call Now
                    </Button>
                  </a>
                )}
                {p.email && (
                  <a href={`mailto:${p.email}`}>
                    <Button variant="outline" className="border-border text-foreground hover:text-foreground hover:bg-muted gap-2" data-testid="email-agent-btn">
                      <Mail size={16} /> Email
                    </Button>
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Specialties */}
      {(p.specialties?.length > 0 || p.service_areas?.length > 0) && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-10" data-testid="agent-specialties">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {p.specialties?.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Specialties</h3>
                <div className="flex flex-wrap gap-2">
                  {p.specialties.map((s, i) => (
                    <Badge key={i} className="bg-amber-500/10 text-amber-400 border-amber-500/20">{s}</Badge>
                  ))}
                </div>
              </div>
            )}
            {p.service_areas?.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Service Areas</h3>
                <div className="flex flex-wrap gap-2">
                  {p.service_areas.map((s, i) => (
                    <Badge key={i} className="bg-blue-500/10 text-blue-400 border-blue-500/20">{s}</Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Featured Listings */}
      {listings.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-10" data-testid="featured-listings">
          <h2 className="text-xl sm:text-2xl font-bold text-foreground mb-6">Featured Listings</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {listings.map(l => {
              const ds = scores[l.id];
              return (
                <Card key={l.id} className="border-border bg-card overflow-hidden hover:border-border transition-colors"
                  data-testid={`listing-card-${l.id}`}>
                  <div className="relative h-44 bg-muted">
                    {l.photo_url ? (
                      <img src={l.photo_url} alt={l.address_line} className="w-full h-full object-cover" loading="lazy" />
                    ) : (
                      <div className="flex items-center justify-center h-full">
                        <Building2 size={32} className="text-muted-foreground" />
                      </div>
                    )}
                    <div className="absolute bottom-2 left-2">
                      <span className="bg-black/70 text-foreground text-sm font-semibold px-2.5 py-1 rounded backdrop-blur-sm">
                        ${l.list_price?.toLocaleString()}
                      </span>
                    </div>
                    {ds && (
                      <div className="absolute top-2 right-2">
                        <div className={`flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded ${
                          ds.opportunity_level === 'exceptional' ? 'bg-amber-500 text-black' :
                          ds.opportunity_level === 'strong' ? 'bg-emerald-500 text-black' :
                          'bg-blue-500 text-white'
                        }`}>
                          <Trophy size={10} /> {ds.score}
                        </div>
                      </div>
                    )}
                  </div>
                  <CardContent className="p-4">
                    <h3 className="text-foreground font-semibold text-sm truncate">{l.address_line}</h3>
                    <p className="text-muted-foreground text-xs mt-0.5">{l.city}, {l.state} {l.postal_code}</p>
                    <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                      {l.beds != null && <span className="flex items-center gap-1"><Bed size={12} /> {l.beds} bd</span>}
                      {l.baths != null && <span className="flex items-center gap-1"><Bath size={12} /> {l.baths} ba</span>}
                      {l.sqft != null && <span className="flex items-center gap-1"><Maximize size={12} /> {l.sqft?.toLocaleString()} sqft</span>}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>
      )}

      {/* Saved Search CTA + Lead Capture Form */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-10" data-testid="lead-capture-section">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* CTA */}
          <div className="flex flex-col justify-center">
            <h2 className="text-xl sm:text-2xl font-bold text-foreground mb-3">
              Looking for Your Perfect Home?
            </h2>
            <p className="text-muted-foreground text-sm sm:text-base mb-6 leading-relaxed">
              Let {p.full_name} help you find exactly what you're looking for in {p.display_market}.
              Share your preferences and get personalized property recommendations delivered straight to your inbox.
            </p>
            <div className="space-y-3">
              {[
                'Personalized property matches based on your criteria',
                'Early access to new listings before they hit the market',
                'Expert local market insights and investment analysis',
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2.5 text-sm text-foreground">
                  <ChevronRight size={16} className="text-amber-400 mt-0.5 shrink-0" />
                  {item}
                </div>
              ))}
            </div>
          </div>

          {/* Contact Form */}
          <Card className="border-border bg-card" data-testid="contact-form-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-foreground text-lg flex items-center gap-2">
                <Send size={18} className="text-amber-400" /> Get in Touch
              </CardTitle>
            </CardHeader>
            <CardContent>
              {sent ? (
                <div className="py-8 text-center" data-testid="contact-success">
                  <CheckCircle2 className="h-10 w-10 text-emerald-400 mx-auto mb-3" />
                  <p className="text-foreground font-medium">{sentMsg}</p>
                  <Button variant="ghost" className="text-muted-foreground mt-4 text-sm" onClick={() => setSent(false)}>
                    Send Another Message
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-3" data-testid="contact-form">
                  <Input placeholder="Your Name *" required value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    className="bg-muted border-border text-foreground placeholder:text-muted-foreground"
                    data-testid="contact-name" />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Input placeholder="Email" type="email" value={form.email}
                      onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                      className="bg-muted border-border text-foreground placeholder:text-muted-foreground"
                      data-testid="contact-email" />
                    <Input placeholder="Phone" type="tel" value={form.phone}
                      onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                      className="bg-muted border-border text-foreground placeholder:text-muted-foreground"
                      data-testid="contact-phone" />
                  </div>
                  <select value={form.interest}
                    onChange={e => setForm(f => ({ ...f, interest: e.target.value }))}
                    className="w-full h-10 rounded-md border border-border bg-muted px-3 text-sm text-foreground"
                    data-testid="contact-interest">
                    <option value="buyer_inquiry">I'm looking to buy</option>
                    <option value="seller_inquiry">I'm looking to sell</option>
                    <option value="investment_inquiry">Investment property</option>
                    <option value="general_inquiry">General question</option>
                  </select>
                  <Textarea placeholder="Tell me what you're looking for..." rows={3} value={form.message}
                    onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                    className="bg-muted border-border text-foreground placeholder:text-muted-foreground"
                    data-testid="contact-message" />
                  <Button type="submit" disabled={sending}
                    className="w-full bg-amber-600 hover:bg-amber-500 text-foreground gap-2"
                    data-testid="contact-submit-btn">
                    {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                    {sending ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border mt-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 text-center">
          <p className="text-muted-foreground text-xs">
            Powered by <span className="text-amber-400 font-medium">{brandName}</span> — AI-Powered Real Estate Platform
          </p>
        </div>
      </footer>
    </div>
  );
}
